/**
 * Weather helpers shared by the home header chip and the weather dashboard:
 * condition → icon, the Woodlands station sensors, forecast shaping and the
 * one-line "Rain from 3pm" hint. React-free so it unit-tests in isolation.
 */
import type { EntityState } from '@home/shared';

/** HA weather conditions → glyphs in the curated set (lib/icons/readings.ts). */
export const CONDITION_ICON: Readonly<Record<string, string>> = {
  sunny: 'sun',
  'clear-night': 'moon',
  partlycloudy: 'cloud-sun',
  cloudy: 'cloud',
  rainy: 'rain',
  pouring: 'rain-heavy',
  lightning: 'storm',
  'lightning-rainy': 'storm-rain',
  snowy: 'snow',
  'snowy-rainy': 'sleet',
  hail: 'hail',
  fog: 'fog',
  windy: 'wind',
  'windy-variant': 'wind',
  exceptional: 'alert',
};

/** Icon for a condition; partly cloudy at night shows the moon behind the cloud. */
export function conditionIcon(condition: string | undefined, night = false): string {
  if (!condition) return 'cloud-sun';
  if (night && condition === 'partlycloudy') return 'cloud-moon';
  if (night && condition === 'sunny') return 'moon';
  return CONDITION_ICON[condition] ?? 'cloud-sun';
}

/** The Woodlands Aercus station (absent elsewhere; callers check the model). */
export const STATION = {
  temp: 'sensor.temperature',
  humidity: 'sensor.humidity',
  dew: 'sensor.dew_point',
  chill: 'sensor.wind_chill',
  wind: 'sensor.wind_speed',
  gust: 'sensor.wind_gust',
  dir: 'sensor.wind_direction',
  uv: 'sensor.uv_index',
  solar: 'sensor.solar_radiation',
  pressure: 'sensor.barometric_pressure',
  rainHour: 'sensor.rain_hour',
  rainDay: 'sensor.rain_day',
  rainWeek: 'sensor.rain_week',
  rainMonth: 'sensor.rain_month',
  rainYear: 'sensor.rain_year',
} as const;

/** The weather entity a home's header and dashboard speak for: weather.home, else the first. */
export function pickWeatherEntity(ids: readonly string[]): string | undefined {
  const w = ids.filter((id) => id.startsWith('weather.'));
  return w.includes('weather.home') ? 'weather.home' : w.sort()[0];
}

// ---------------------------------------------------------------- forecasts

export interface ForecastRow {
  datetime: string;
  condition?: string;
  temperature?: number;
  templow?: number;
  apparent_temperature?: number;
  precipitation?: number;
  precipitation_probability?: number;
  is_daytime?: boolean;
}

const WET = new Set(['rainy', 'pouring', 'lightning-rainy', 'snowy', 'snowy-rainy', 'hail']);
const SNOW = new Set(['snowy', 'snowy-rainy', 'hail']);
const STORM = new Set(['lightning', 'lightning-rainy']);

/** An hour counts as wet at ≥50% chance, ≥0.3 mm, or a wet condition. */
export function isWetHour(r: ForecastRow): boolean {
  if (r.precipitation_probability !== undefined && r.precipitation_probability >= 50) return true;
  if (r.precipitation !== undefined && r.precipitation >= 0.3) return true;
  // A wet condition with an explicit low chance (Met Office "light rain, 20%") isn't a rain call.
  if (r.precipitation_probability !== undefined && r.precipitation_probability < 30) return false;
  return WET.has(r.condition ?? '');
}

/** The forecast's hours from the current one onwards, at most `n`. */
export function upcomingHours(rows: readonly ForecastRow[], now: number, n = 12): ForecastRow[] {
  return rows.filter((r) => Date.parse(r.datetime) > now - 3_600_000).slice(0, n);
}

/** Hour of day (0–23) in the home's timezone. */
export function hourIn(epochMs: number, timeZone?: string): number {
  try {
    const h = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', ...(timeZone ? { timeZone } : {}) }).format(epochMs);
    return Number(h) % 24;
  } catch {
    return new Date(epochMs).getHours();
  }
}

/** "3pm", "11am", "noon", "midnight". */
export function hourLabel(epochMs: number, timeZone?: string): string {
  const h = hourIn(epochMs, timeZone);
  if (h === 0) return 'midnight';
  if (h === 12) return 'noon';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

/** "2026-10-05" in the home's timezone. */
export function dayKey(epochMs: number, timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(epochMs);
  } catch {
    return new Date(epochMs).toISOString().slice(0, 10);
  }
}

/**
 * The short line under the temperature: "Rain from 3pm", "Rain until 4pm",
 * "Snow from 9pm", "Storms from 6pm", "Rain soon", "Dry next 12 h".
 * `hours` must start at the current hour. `rainingNow` comes from the station
 * (rain this hour) or the current condition.
 */
export function forecastHint(hours: readonly ForecastRow[], opts: { now: number; timeZone?: string; rainingNow?: boolean }): string | undefined {
  if (hours.length === 0) return opts.rainingNow ? 'Raining now' : undefined;
  const wetIdx = hours.findIndex(isWetHour);
  const at = (r: ForecastRow): string => hourLabel(Date.parse(r.datetime), opts.timeZone);
  if (opts.rainingNow || (wetIdx === 0 && Date.parse(hours[0]!.datetime) <= opts.now)) {
    const start = wetIdx === 0 ? 0 : -1;
    const dryIdx = hours.findIndex((r, i) => i > start && !isWetHour(r));
    if (dryIdx === -1) return `Rain for ${hours.length} h+`;
    return dryIdx === 0 ? 'Rain easing' : `Rain until ${at(hours[dryIdx]!)}`;
  }
  if (wetIdx === -1) return `Dry next ${hours.length} h`;
  const r = hours[wetIdx]!;
  const kind = STORM.has(r.condition ?? '') ? 'Storms' : SNOW.has(r.condition ?? '') ? 'Snow' : 'Rain';
  if (Date.parse(r.datetime) - opts.now < 45 * 60_000) return `${kind} soon`;
  return `${kind} from ${at(r)}`;
}

/** Peak chance of rain over the hours, or total mm when the source has no chance (met.no). */
export function rainOutlook(hours: readonly ForecastRow[]): { kind: 'chance'; value: number } | { kind: 'mm'; value: number } | undefined {
  const chances = hours.flatMap((r) => (r.precipitation_probability !== undefined ? [r.precipitation_probability] : []));
  if (chances.length > 0) return { kind: 'chance', value: Math.round(Math.max(...chances)) };
  const mm = hours.flatMap((r) => (r.precipitation !== undefined ? [r.precipitation] : []));
  if (mm.length > 0) return { kind: 'mm', value: Math.round(mm.reduce((a, b) => a + b, 0) * 10) / 10 };
  return undefined;
}

/**
 * Today's high and low: the daily forecast's entry for today, widened by the
 * hourly rows still to come today and the current reading.
 */
export function todayRange(
  daily: readonly ForecastRow[] | undefined,
  hourly: readonly ForecastRow[] | undefined,
  current: number | undefined,
  now: number,
  timeZone?: string,
): { hi: number; lo: number } | undefined {
  const today = dayKey(now, timeZone);
  const vals: number[] = [];
  const d = daily?.find((r) => dayKey(Date.parse(r.datetime), timeZone) === today);
  if (d?.temperature !== undefined) vals.push(d.temperature);
  if (d?.templow !== undefined) vals.push(d.templow);
  for (const r of hourly ?? []) if (r.temperature !== undefined && dayKey(Date.parse(r.datetime), timeZone) === today) vals.push(r.temperature);
  if (current !== undefined) vals.push(current);
  if (vals.length < 2) return undefined;
  return { hi: Math.max(...vals), lo: Math.min(...vals) };
}

/** True when a state is a usable, recent reading (station sensors go stale when the base drops off Wi-Fi). */
export function fresh(s: EntityState | undefined, now: number, maxAgeSec = 2 * 3600): boolean {
  if (!s || s.s === 'unavailable' || s.s === 'unknown' || s.s === '') return false;
  return now / 1000 - s.lu <= maxAgeSec;
}

// ---------------------------------------------------------------- client cache

export type ForecastType = 'daily' | 'hourly';
const TTL_MS = 15 * 60_000;
const LS_PREFIX = 'eh.forecast.';

interface Cached {
  at: number;
  rows: ForecastRow[];
}

const mem = new Map<string, Cached>();
const inflight = new Map<string, Promise<ForecastRow[]>>();

const key = (home: string, id: string, type: ForecastType): string => `${home}|${id}|${type}`;

/** The cached forecast if any (fresh or not), for an instant first paint. */
export function cachedForecast(home: string, id: string, type: ForecastType): Cached | undefined {
  const k = key(home, id, type);
  const hit = mem.get(k);
  if (hit) return hit;
  try {
    const raw = localStorage.getItem(LS_PREFIX + k);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as Cached;
    if (typeof v?.at !== 'number' || !Array.isArray(v.rows)) return undefined;
    mem.set(k, v);
    return v;
  } catch {
    return undefined;
  }
}

export const isStale = (c: Cached | undefined, now = Date.now()): boolean => !c || now - c.at > TTL_MS;

/**
 * Loads a forecast through `fetcher` (store.call → weather.get_forecasts), at
 * most once per 15 minutes per home/entity/type, deduping concurrent callers.
 */
export function loadForecast(
  home: string,
  id: string,
  type: ForecastType,
  fetcher: () => Promise<unknown>,
  now = Date.now(),
): Promise<ForecastRow[]> {
  const k = key(home, id, type);
  const c = cachedForecast(home, id, type);
  if (c && !isStale(c, now)) return Promise.resolve(c.rows);
  const pending = inflight.get(k);
  if (pending) return pending;
  const p = fetcher()
    .then((res) => {
      const f = (res as Record<string, { forecast?: ForecastRow[] }> | null)?.[id]?.forecast;
      const rows = Array.isArray(f) ? f : [];
      const v: Cached = { at: Date.now(), rows };
      mem.set(k, v);
      try {
        localStorage.setItem(LS_PREFIX + k, JSON.stringify(v));
      } catch {
        /* storage full or blocked: memory cache still works */
      }
      return rows;
    })
    .finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
}

/** Test hook. */
export function clearForecastCache(): void {
  mem.clear();
  inflight.clear();
}
